import { useMemo, useState } from "react";
import { useAsync } from "react-use";
import { jt, t } from "ttag";

import {
  useUndoUnsubscribeFromNotificationByEmailMutation,
  useUndoUnsubscribeFromPulseMutation,
  useUnsubscribeFromNotificationByEmailMutation,
  useUnsubscribeFromPulseMutation,
} from "metabase/api";
import { NotFound } from "metabase/common/components/ErrorPages";
import { ExternalLink } from "metabase/common/components/ExternalLink";
import { LighthouseIllustration } from "metabase/common/components/LighthouseIllustration";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { LogoIcon } from "metabase/common/components/LogoIcon";
import { MetabotLogo } from "metabase/common/components/MetabotLogo";
import { useSelector } from "metabase/redux";
import { useSearchParams } from "metabase/router";
import { getLoginPageIllustration } from "metabase/selectors/whitelabel";
import { Box, Button, Center, Flex, Icon, Stack, Text } from "metabase/ui";

import S from "./Unsubscribe.module.css";

const ERRORS = {
  MISSING_REQUIRED_PARAMETERS: "missing required parameters",
} as const;

const SUBSCRIPTION = {
  UNSUBSCRIBE: "unsubscribe",
  RESUBSCRIBE: "resubscribe",
} as const;

type Subscription = (typeof SUBSCRIPTION)[keyof typeof SUBSCRIPTION];

export const UnsubscribePage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const [subscriptionChange, setSubscriptionChange] = useState<Subscription>(
    SUBSCRIPTION.UNSUBSCRIBE,
  );

  const hash = searchParams.get("hash") ?? undefined;
  const email = searchParams.get("email") ?? undefined;
  const pulseId = searchParams.get("pulse-id") ?? undefined;
  const notificationHandlerId =
    searchParams.get("notification-handler-id") ?? undefined;

  const { data, isLoading, error } = useUnsubscribeRequest({
    hash,
    email,
    pulseId,
    notificationHandlerId,
    subscriptionChange,
  });

  if (error || !email) {
    if (error?.message === ERRORS.MISSING_REQUIRED_PARAMETERS) {
      return <NotFound />;
    }

    return (
      <UnsubscribeRoot>
        <ErrorDisplay />
      </UnsubscribeRoot>
    );
  }

  if (isLoading) {
    return (
      <UnsubscribeRoot>
        <LoadingAndErrorWrapper loading={isLoading} />
      </UnsubscribeRoot>
    );
  }

  return (
    <UnsubscribeRoot>
      {subscriptionChange === SUBSCRIPTION.UNSUBSCRIBE ? (
        <SuccessfulUnsubscribe
          email={email}
          alertTitle={data?.title}
          action={() => setSubscriptionChange(SUBSCRIPTION.RESUBSCRIBE)}
        />
      ) : (
        <SuccessfulResubscribe
          email={email}
          alertTitle={data?.title}
          action={() => setSubscriptionChange(SUBSCRIPTION.UNSUBSCRIBE)}
        />
      )}
    </UnsubscribeRoot>
  );
};

function SuccessfulUnsubscribe({
  email,
  alertTitle,
  action,
}: SubscriptionDetailProps) {
  return (
    <SuccessfulRequestWrapper
      text={jt`You've unsubscribed ${(
        <ExternalLink key="link" href={`mailto:${email}`}>
          {email}
        </ExternalLink>
      )} from the "${alertTitle}" alert.`}
      buttonText={t`Undo`}
      action={action}
    />
  );
}

function SuccessfulResubscribe({
  email,
  alertTitle,
  action,
}: SubscriptionDetailProps) {
  return (
    <SuccessfulRequestWrapper
      text={jt`Okay, ${(
        <ExternalLink key="link" href={`mailto:${email}`}>
          {email}
        </ExternalLink>
      )} is subscribed to the "${alertTitle}" alert again.`}
      buttonText={t`Unsubscribe`}
      action={action}
    />
  );
}

function SuccessfulRequestWrapper({
  text,
  buttonText,
  action,
}: SubscriptionWrapperProps) {
  return (
    <Stack align="center">
      <Center bg="background_surface-brand-subtle" p="lg" bdrs="100%">
        <Icon name="check" size={30} c="core-brand" />
      </Center>
      <Text fw={700} c="text-secondary" mb="0.75rem" ta="center">
        {text}
      </Text>
      <Button variant="filled" onClick={action}>
        {buttonText}
      </Button>
    </Stack>
  );
}

function useUnsubscribeRequest({
  hash,
  email,
  pulseId,
  notificationHandlerId,
  subscriptionChange,
}: UseUnsubscribeProps): UseUnsubscribeResult {
  const params = useMemo(() => {
    if (!hash || !email) {
      return undefined;
    }

    if (pulseId) {
      return {
        hash,
        email,
        "pulse-id": pulseId,
      };
    }

    if (notificationHandlerId) {
      return {
        hash,
        email,
        "notification-handler-id": notificationHandlerId,
      };
    }

    return undefined;
  }, [hash, email, pulseId, notificationHandlerId]);

  const [unsubscribeFromPulse] = useUnsubscribeFromPulseMutation();
  const [undoUnsubscribeFromPulse] = useUndoUnsubscribeFromPulseMutation();
  const [unsubscribeFromNotification] =
    useUnsubscribeFromNotificationByEmailMutation();
  const [undoUnsubscribeFromNotification] =
    useUndoUnsubscribeFromNotificationByEmailMutation();

  const {
    value: data,
    loading: isLoading,
    error,
  } = useAsync(async () => {
    if (!params) {
      throw new Error(ERRORS.MISSING_REQUIRED_PARAMETERS);
    }

    const isUnsubscribe = subscriptionChange === SUBSCRIPTION.UNSUBSCRIBE;

    const triggers = notificationHandlerId
      ? {
          unsubscribe: unsubscribeFromNotification,
          undo: undoUnsubscribeFromNotification,
        }
      : { unsubscribe: unsubscribeFromPulse, undo: undoUnsubscribeFromPulse };

    const unsubscribe = isUnsubscribe ? triggers.unsubscribe : triggers.undo;

    return await unsubscribe(params).unwrap();
  }, [
    params,
    subscriptionChange,
    notificationHandlerId,
    unsubscribeFromPulse,
    undoUnsubscribeFromPulse,
    unsubscribeFromNotification,
    undoUnsubscribeFromNotification,
  ]);

  return { data, isLoading, error };
}

function UnsubscribeRoot({ children }: { children: JSX.Element }) {
  const loginPageIllustration = useSelector(getLoginPageIllustration);
  return (
    <Flex pos="relative" mih="100%" bg="background_page-secondary">
      {loginPageIllustration &&
        (loginPageIllustration.isDefault ? (
          <LighthouseIllustration />
        ) : (
          <Box
            data-testid="unsubscribe-page-illustration"
            pos="absolute"
            inset={0}
            bgsz="100% auto"
            bgr="no-repeat"
            bgp="right bottom"
            style={{ backgroundImage: `url("${loginPageIllustration.src}")` }}
          />
        ))}
      <Box pos="relative" flex={1}>
        <Center mih={"100%"} miw={"100%"}>
          <Stack>
            <LogoIcon height={65}></LogoIcon>
            <Flex
              className={S.shadow}
              direction="column"
              align="center"
              justify="center"
              w={{ base: "100%", sm: "30.875rem" }}
              miw="35rem"
              mih="20rem"
              mt="xl"
              py="xxxl"
              px={{ base: "xl", sm: "3.5rem" }}
              bg="background_page-primary"
              bdrs="xs"
            >
              {children}
            </Flex>
          </Stack>
        </Center>
      </Box>
    </Flex>
  );
}

function ErrorDisplay() {
  return (
    <Stack align="center" gap="xxs" aria-label="error message">
      <MetabotLogo className={S.logo} variant="sad" />
      <Text
        fw={700}
        fz="md"
        mt={"1.5rem"}
      >{t`Whoops, something went wrong.`}</Text>
      <Text fz="md">{t`Please give it a minute and try again`}</Text>
    </Stack>
  );
}

interface SubscriptionDetailProps {
  email: string;
  alertTitle: string | undefined;
  action: () => void;
}

interface SubscriptionWrapperProps {
  text: string | string[];
  buttonText: string;
  action: () => void;
}

interface UseUnsubscribeProps {
  hash: string | undefined;
  email: string | undefined;
  pulseId: string | undefined;
  notificationHandlerId: string | undefined;
  subscriptionChange: Subscription;
}

interface UseUnsubscribeResult {
  isLoading: boolean;
  error?: Error | undefined;
  data?: { title: string };
}
